import { useEffect, useRef, useState } from 'react';
import { PhoneIcon, PhoneXMarkIcon, SpeakerWaveIcon, SpeakerXMarkIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import Avatar from '../../components/Avatar';
import { useVoiceStore } from '../../stores/voiceStore';
import { voiceService } from './services/voiceService';
import { answerOffer, teardownPeer } from './webrtc';
import { twilioClient } from './twilio-client';

const RING_TIMEOUT_MS = 30_000;
const RINGTONE_PATH = '/sounds/ringtone.mp3';

export default function IncomingCallToast() {
  const incomingCall = useVoiceStore((s) => s.incomingCall);
  const availableForCalls = useVoiceStore((s) => s.availableForCalls);
  const setActive = useVoiceStore((s) => s.setActive);
  const clearIncoming = useVoiceStore((s) => s.clearIncoming);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [muted, setMuted] = useState(false);
  const [answering, setAnswering] = useState(false);

  // Render gate: must have a live call AND user must be available.
  const visible = !!incomingCall && availableForCalls;

  // Ringtone playback.
  useEffect(() => {
    const el = audioRef.current;
    if (!visible || !el || muted) {
      el?.pause();
      return;
    }
    el.currentTime = 0;
    el.loop = true;
    // Browsers may reject autoplay until user interaction; fail silently.
    el.play().catch(() => {
      /* user gesture required — silent fail */
    });
    return () => {
      el.pause();
    };
  }, [visible, muted]);

  // 30s auto-dismiss → mark as missed locally.
  useEffect(() => {
    if (!visible || !incomingCall) return;
    const callId = incomingCall.id;
    const handle = window.setTimeout(() => {
      // Only clear if the same call is still ringing.
      const current = useVoiceStore.getState().incomingCall;
      if (current && current.id === callId) {
        useVoiceStore.getState().clearIncoming();
      }
    }, RING_TIMEOUT_MS);
    return () => window.clearTimeout(handle);
  }, [visible, incomingCall]);

  if (!visible || !incomingCall) return null;

  const handleAnswer = async () => {
    if (answering) return;
    setAnswering(true);

    if (incomingCall.transport === 'twilio') {
      // Twilio path: the SDK already has the Call object via its `incoming`
      // event. Accept it and stash it on the active-call state. The SDK
      // handles audio internally — no SDP, no MediaStream.
      try {
        const call = await twilioClient.acceptIncoming();
        if (!call) {
          throw new Error(
            'Twilio call did not arrive at the SDK in time. Check the ' +
              'Twilio Console call log to see whether the Dial fired.',
          );
        }
        setActive({
          call: incomingCall,
          transport: 'twilio',
          twilioCall: call,
          answeredAt: new Date().toISOString(),
        });
        clearIncoming();
      } catch (err) {
        console.warn('[voice] failed to accept Twilio call:', err);
        toast.error('Failed to answer call');
        clearIncoming();
      } finally {
        setAnswering(false);
      }
      return;
    }

    // WebRTC path (existing flow).
    let answeredPeer: Awaited<ReturnType<typeof answerOffer>> | null = null;
    try {
      answeredPeer = await answerOffer(incomingCall.offerSdp, (stream) => {
        // Track binding happens via the store consumer (InCallPanel).
        useVoiceStore.setState((state) => {
          if (!state.activeCall) return state;
          return {
            ...state,
            activeCall: { ...state.activeCall, remoteStream: stream },
          };
        });
      });

      const response = await voiceService.answerCall(
        incomingCall.id,
        answeredPeer.sdpAnswer
      );

      setActive({
        call: incomingCall,
        transport: 'webrtc',
        pc: answeredPeer.pc,
        remoteStream: answeredPeer.remoteStream,
        localStream: answeredPeer.localStream,
        answeredAt: response.answeredAt,
      });
      clearIncoming();
    } catch (err: unknown) {
      // Server returned 409 (someone else won) or media/SDP failure.
      teardownPeer(answeredPeer?.pc ?? null, answeredPeer?.localStream ?? null);

      const status =
        typeof err === 'object' && err !== null && 'response' in err
          ? // axios-style
            ((err as { response?: { status?: number } }).response?.status ?? 0)
          : 0;

      if (status === 409) {
        toast('Another agent answered first', { icon: '📞' });
      } else {
        toast.error('Failed to answer call');
      }
      clearIncoming();
    } finally {
      setAnswering(false);
    }
  };

  const handleDecline = () => {
    // Backend handles "missed" status via Meta's terminate event when the
    // customer hangs up or the timeout expires. Decline just clears locally
    // — the toast won't reappear because `incomingCall` is null and we won't
    // re-receive the same `call:incoming` for this id.
    clearIncoming();
  };

  return (
    <div className="pointer-events-none fixed right-4 top-4 z-[60] w-80 max-w-[calc(100vw-2rem)] animate-slide-in">
      <audio ref={audioRef} src={RINGTONE_PATH} preload="none" />
      <div className="pointer-events-auto overflow-hidden rounded-card border border-line bg-surface shadow-l">
        <div className="flex items-center justify-between border-b border-line px-4 py-2">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-brand">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-signal" />
            </span>
            Incoming call
          </div>
          <button
            onClick={() => setMuted((m) => !m)}
            className="rounded p-1 text-ink-3 hover:bg-black/[0.04] hover:text-ink"
            aria-label={muted ? 'Unmute ringtone' : 'Mute ringtone'}
            type="button"
          >
            {muted ? (
              <SpeakerXMarkIcon className="h-4 w-4" />
            ) : (
              <SpeakerWaveIcon className="h-4 w-4" />
            )}
          </button>
        </div>

        <div className="flex items-center gap-3 px-4 py-3">
          <Avatar name={incomingCall.customerName || incomingCall.customerPhone} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink">
              {incomingCall.customerName || 'Unknown caller'}
            </p>
            <p className="truncate font-mono text-xs text-ink-2">
              {incomingCall.customerPhone}
            </p>
            <p className="mt-0.5 text-[10px] uppercase tracking-wide text-ink-3">
              via {incomingCall.transport}
            </p>
          </div>
        </div>

        <div className="flex gap-2 border-t border-line bg-surface-2 px-4 py-3">
          <button
            onClick={handleDecline}
            disabled={answering}
            type="button"
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-pill bg-surface border border-line px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-black/[0.04] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PhoneXMarkIcon className="h-4 w-4" />
            Decline
          </button>
          <button
            onClick={handleAnswer}
            disabled={answering}
            type="button"
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-pill bg-brand px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-ink disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PhoneIcon className="h-4 w-4" />
            {answering ? 'Connecting...' : 'Answer'}
          </button>
        </div>
      </div>
    </div>
  );
}
